import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Clock,
  Download,
  Mail,
  Play,
  Loader2,
  ArrowRight,
  ShoppingCart,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { API_BASE } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import ContactFormDialog from "../components/ContactFormDialog";
import { areaLabel } from "../lib/courseAreas";

const LEVEL_LABEL = { base: "Base", intermedio: "Intermedio", avanzato: "Avanzato" };

function formatEUR(v) {
  try {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
    }).format(v);
  } catch {
    return `€ ${Number(v).toFixed(2)}`;
  }
}

export default function PublicCourseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addItem } = useCart();
  const [course, setCourse] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | not_found
  const [downloading, setDownloading] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);

  const handlePurchase = () => {
    // Non autenticato → registrazione.
    if (!user) { navigate("/registrati"); return; }
    if (user.role === "studente" && user.organization_id) {
      toast.info("I discenti aziendali ricevono i corsi dal referente della propria azienda.");
      return;
    }
    if (user.role !== "azienda" && user.role !== "studente") {
      toast.info("L'acquisto è disponibile per privati e aziende.");
      return;
    }
    // Privato o azienda → aggiungi al carrello e vai al carrello.
    addItem(
      { id, title: course.title, price: course.price, area: course.area, level: course.level },
      1
    );
    toast.success("Corso aggiunto al carrello.");
    navigate("/carrello");
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/public/catalog/${id}`);
        if (!cancelled) {
          setCourse(data);
          setStatus("ok");
        }
      } catch {
        if (!cancelled) setStatus("not_found");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await fetch(
        `${API_BASE}/public/catalog/${id}/enrollment-form?t=${Date.now()}`,
        { method: "GET" }
      );
      if (!res.ok) throw new Error("Download fallito");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // Derive filename from content-disposition when possible
      const cd = res.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : `Modulo_Iscrizione.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.message || "Errore durante il download");
    } finally {
      setDownloading(false);
    }
  };

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 size={20} className="animate-spin text-brand-blue" />
      </div>
    );
  }
  if (status === "not_found" || !course) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center text-center px-4">
        <div className="wordmark text-brand-blue text-2xl mb-2">
          Corso non disponibile
        </div>
        <p className="text-brand-muted mb-6">
          Il corso richiesto non è più pubblicato nel catalogo.
        </p>
        <Link to="/corsi" className="btn-primary inline-flex items-center gap-2">
          Torna al catalogo <ArrowRight size={16} />
        </Link>
      </div>
    );
  }

  const durationStr =
    course.duration_hours != null ? `${course.duration_hours} ore` : "durata da definire";

  return (
    <div data-testid="public-course-detail" className="min-h-screen bg-white">
      {/* Topbar (same style as PublicCatalog) */}
      <header
        data-testid="catalog-topbar"
        className="bg-white border-b border-brand-line h-16 flex items-center justify-between px-4 sm:px-6 lg:px-8 sticky top-0 z-40"
      >
        <Link to="/" className="flex items-center gap-2.5">
          <img
            src={`${process.env.PUBLIC_URL}/logo-academy-full.png`}
            alt="Buro Academy"
            className="h-8 w-auto"
          />
        </Link>
        <nav className="flex items-center gap-4">
          <Link to="/corsi" className="hidden sm:inline text-sm font-semibold text-brand-blue">
            Catalogo Corsi
          </Link>
          <Link to="/login" className="btn-primary text-sm inline-flex items-center gap-2">
            Accedi <ArrowRight size={16} />
          </Link>
        </nav>
      </header>

      {/* Hero */}
      <section className="brand-grid-bg border-b border-brand-line">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <button
            onClick={() => navigate("/corsi")}
            className="inline-flex items-center gap-1.5 text-sm text-brand-blue hover:underline underline-offset-4 mb-6"
            data-testid="course-back-to-catalog"
          >
            <ArrowLeft size={16} /> Torna al catalogo
          </button>

          <div className="grid lg:grid-cols-[1fr_320px] gap-8 items-start">
            <div>
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="text-[10px] uppercase tracking-widest bg-brand-blue/5 text-brand-blue border border-brand-blue/15 rounded-sm px-2 py-1">
                  {areaLabel(course.area)}
                </span>
                <span className="text-[10px] uppercase tracking-widest bg-brand-gold/10 text-brand-gold-deep border border-brand-gold/30 rounded-sm px-2 py-1">
                  Livello: {LEVEL_LABEL[course.level] || course.level}
                </span>
                <span className="text-[10px] uppercase tracking-widest inline-flex items-center gap-1 text-brand-muted">
                  <Clock size={11} /> {durationStr}
                </span>
                {course.ai_generated_content && (
                  <span
                    data-testid="detail-ai-badge"
                    className="text-[10px] uppercase tracking-widest inline-flex items-center gap-1 bg-violet-50 text-violet-700 border border-violet-200 rounded-sm px-2 py-1"
                  >
                    <Sparkles size={11} /> Presenti contenuti generati con IA
                  </span>
                )}
              </div>
              <h1
                data-testid="course-title"
                className="text-3xl sm:text-4xl lg:text-5xl leading-[1.1] text-brand-ink mb-5"
              >
                {course.title}
              </h1>
              {course.short_description && (
                <p className="text-lg text-brand-muted leading-relaxed max-w-2xl">
                  {course.short_description}
                </p>
              )}
            </div>

            {/* Price card */}
            <aside className="bg-white border border-brand-blue/20 rounded-sm p-6 shadow-sm">
              <div className="text-xs uppercase tracking-widest text-brand-muted mb-1">
                Prezzo base per addetto
              </div>
              <div
                data-testid="course-price"
                className="text-4xl font-bold text-brand-blue leading-none mb-1"
              >
                {formatEUR(course.price)}
              </div>
              <div className="text-xs text-brand-muted mb-5">
                IVA esclusa · sconti per gruppi nel modulo
              </div>
              <button
                onClick={handlePurchase}
                data-testid="course-buy-btn"
                className="btn-primary w-full inline-flex items-center justify-center gap-2"
              >
                <ShoppingCart size={14} />
                Aggiungi al carrello
              </button>
              <button
                onClick={downloadPdf}
                disabled={downloading}
                data-testid="download-enrollment-btn"
                className="w-full mt-2 inline-flex items-center justify-center gap-2 rounded-sm border border-brand-blue text-brand-blue hover:bg-brand-blue/5 px-4 py-2.5 font-semibold text-sm transition-colors disabled:opacity-60"
              >
                {downloading ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Download size={14} />
                )}
                Scarica modulo di iscrizione
              </button>
              <button
                onClick={() => setContactOpen(true)}
                data-testid="course-contact-btn"
                className="w-full mt-2 inline-flex items-center justify-center gap-2 rounded-sm border border-brand-blue text-brand-blue hover:bg-brand-blue/5 px-4 py-2.5 font-semibold text-sm transition-colors"
              >
                <Mail size={14} /> Contatta l'amministrazione
              </button>
            </aside>
          </div>
        </div>
      </section>

      {/* Description + modules */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid lg:grid-cols-[1fr_320px] gap-10">
        <div>
          <h2 className="text-2xl text-brand-blue mb-4">Descrizione del corso</h2>
          <div
            data-testid="course-description"
            className="text-brand-ink leading-relaxed whitespace-pre-line"
          >
            {course.description || course.short_description || "—"}
          </div>

          <h2 className="text-2xl text-brand-blue mt-10 mb-4">Programma</h2>
          {course.modules_preview?.length ? (
            <ol
              data-testid="course-modules-preview"
              className="border border-brand-line rounded-sm divide-y divide-brand-line"
            >
              {course.modules_preview.map((m, i) => (
                <li
                  key={i}
                  data-testid={`course-module-${i}`}
                  className="p-4 flex items-start gap-3 bg-white"
                >
                  <div className="shrink-0 inline-flex h-7 w-7 items-center justify-center rounded-sm bg-brand-blue/5 text-brand-blue text-xs font-semibold">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-brand-ink">
                      {m.title}
                    </div>
                    <div className="text-xs text-brand-muted mt-0.5 inline-flex items-center gap-1">
                      <Play size={11} /> {m.lessons_count} lezion
                      {m.lessons_count === 1 ? "e" : "i"}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          ) : (
            <div className="text-sm text-brand-muted">
              Programma dettagliato in fase di preparazione.
            </div>
          )}
        </div>

        <aside className="space-y-4">
          <div className="bg-brand-bg border border-brand-line rounded-sm p-5">
            <div className="text-xs uppercase tracking-widest text-brand-gold mb-2">
              Info
            </div>
            <ul className="text-sm text-brand-ink space-y-2">
              <li>
                <strong>Area</strong>: {areaLabel(course.area)}
              </li>
              <li>
                <strong>Livello</strong>: {LEVEL_LABEL[course.level] || course.level}
              </li>
              <li>
                <strong>Durata</strong>: {durationStr}
              </li>
              <li>
                <strong>Modalità</strong>: online, on-demand
              </li>
              <li>
                <strong>Prezzo</strong>: {formatEUR(course.price)} IVA esclusa
              </li>
            </ul>
          </div>
        </aside>
      </section>

      <ContactFormDialog
        open={contactOpen}
        onOpenChange={setContactOpen}
        presetCourseId={course.id}
        presetCourseTitle={course.title}
      />
    </div>
  );
}
